import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {ResizeObserver | undefined} */
  #masonryResizeObserver;

  /** @type {MutationObserver | undefined} */
  #masonryMutationObserver;

  /** @type {number | undefined} */
  #masonryAnimationFrame;

  /** @type {number | undefined} */
  #imagePreloadTimer;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#setupMasonryLayout();

    if (this.hasAttribute('preload-product-images')) {
      this.#imagePreloadTimer = window.setTimeout(this.#preloadProductImages, 500);
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#masonryResizeObserver?.disconnect();
    this.#masonryMutationObserver?.disconnect();

    if (this.#masonryAnimationFrame) cancelAnimationFrame(this.#masonryAnimationFrame);
    if (this.#imagePreloadTimer) window.clearTimeout(this.#imagePreloadTimer);
  }

  #preloadProductImages = () => {
    const pendingImages = Array.from(
      this.querySelectorAll(
        'product-card slideshow-slide[aria-hidden="false"] img[loading="lazy"]:not([data-preload-requested])'
      )
    );

    for (const image of pendingImages.slice(0, 4)) {
      if (!(image instanceof HTMLImageElement)) continue;

      image.dataset.preloadRequested = 'true';
      image.decoding = 'async';
      image.loading = 'eager';
    }

    if (pendingImages.length > 4) {
      this.#imagePreloadTimer = window.setTimeout(this.#preloadProductImages, 150);
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#scheduleMasonryLayout();
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  #setupMasonryLayout() {
    const { grid } = this.refs;
    if (!grid) return;

    this.#masonryResizeObserver = new ResizeObserver(this.#scheduleMasonryLayout);
    this.#masonryMutationObserver = new MutationObserver(() => {
      this.#observeMasonryCards();
      this.#scheduleMasonryLayout();
    });

    this.#masonryMutationObserver.observe(grid, { childList: true });
    this.#observeMasonryCards();
    this.#scheduleMasonryLayout();
  }

  #observeMasonryCards() {
    const { grid } = this.refs;
    if (!grid || !this.#masonryResizeObserver) return;

    this.#masonryResizeObserver.disconnect();
    this.#masonryResizeObserver.observe(grid);

    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    for (const card of cards) {
      this.#masonryResizeObserver.observe(card);
    }
  }

  #scheduleMasonryLayout = () => {
    if (this.#masonryAnimationFrame) cancelAnimationFrame(this.#masonryAnimationFrame);

    this.#masonryAnimationFrame = requestAnimationFrame(() => {
      this.#masonryAnimationFrame = undefined;
      this.#layoutMasonryGrid();
    });
  };

  #layoutMasonryGrid() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    this.#resetMasonryStyles(grid, cards);

    if (grid.getAttribute('product-grid-view') !== 'default') {
      grid.removeAttribute('data-masonry-pending');
      return;
    }

    const styles = getComputedStyle(grid);
    const columns = styles.gridTemplateColumns.split(' ').filter((column) => column !== 'none');
    const columnCount = columns.length;

    if (columnCount < 2 || cards.length < 2) {
      grid.removeAttribute('data-masonry-pending');
      return;
    }

    const columnGap = parseFloat(styles.columnGap) || 0;
    const rowGap = parseFloat(styles.rowGap) || 0;
    const columnWidth = (grid.clientWidth - columnGap * (columnCount - 1)) / columnCount;
    const columnHeights = Array(columnCount).fill(0);
    const trailingItemsCount = cards.length % columnCount;
    const trailingStartIndex = trailingItemsCount > 0 ? cards.length - trailingItemsCount : -1;

    for (const [index, card] of cards.entries()) {
      if (!(card instanceof HTMLElement)) continue;

      card.style.width = `${columnWidth}px`;
      // Keep masonry behavior, but left-align trailing cards in an incomplete last row.
      const isTrailingCard = trailingItemsCount > 0 && index >= trailingStartIndex;
      const trailingColumnIndex = index - trailingStartIndex;
      const columnIndex = isTrailingCard ? trailingColumnIndex : columnHeights.indexOf(Math.min(...columnHeights));
      const x = columnIndex * (columnWidth + columnGap);
      const y = columnHeights[columnIndex];

      card.style.transform = `translate(${x}px, ${y}px)`;
      columnHeights[columnIndex] += card.getBoundingClientRect().height + rowGap;
    }

    const gridHeight = Math.max(...columnHeights) - rowGap;
    grid.style.height = `${Math.max(gridHeight, 0)}px`;
    grid.setAttribute('data-masonry-ready', 'true');
    grid.removeAttribute('data-masonry-pending');
  }

  /**
   * @param {HTMLElement} grid
   * @param {Element[]} cards
   */
  #resetMasonryStyles(grid, cards) {
    grid.removeAttribute('data-masonry-ready');
    grid.style.height = '';

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      card.style.width = '';
      card.style.transform = '';
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
